import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { adminFetch } from '@/hooks/admin/useAdminApi';

/** Which sections the public chain form renders. */
export interface OnboardingAskFor {
  store_code: boolean;
  store_phone: boolean;
  gm_contact: boolean;
  open_date: boolean;
  delivery_days: boolean;
  special_instructions: boolean;
  terms: boolean;
  signature: boolean;
  /** Equipment interest + the ERLS lease handoff. Franchise links default on. */
  equipment: boolean;
}

export interface OnboardingLink {
  id: string;
  token: string;
  label: string | null;
  ask_for: OnboardingAskFor;
  active: boolean;
  expires_at: string | null;
  uses_count: number;
  last_used_at: string | null;
  created_by: string | null;
  created_at: string;
  state: 'ok' | 'inactive' | 'expired';
  /** The full public URL to hand out. */
  url: string;
}

/** One chain or franchise company on the links console. */
export interface OnboardingLinkGroup {
  customer_id: string;
  name: string;
  qbo_customer_id: number | null;
  /** null = a QBO parent that hasn't been marked as a group yet. */
  group_kind: 'chain' | 'franchise' | null;
  group_brand: string | null;
  /** Sub-customers in QuickBooks — the stores this group could onboard. */
  member_count: number;
  link: OnboardingLink | null;
  retired_links: number;
}

const key = (masterCustomerId: string | undefined) => ['admin-onboarding-links', masterCustomerId];
const ALL_KEY = ['admin-onboarding-links', 'all'];

export function useOnboardingLinks(masterCustomerId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: key(masterCustomerId),
    queryFn: () =>
      adminFetch<{ ok: true; links: OnboardingLink[] }>(
        `admin-onboarding-links?master_customer_id=${masterCustomerId}`,
      ),
    enabled: !!masterCustomerId && enabled,
    staleTime: 30_000,
  });
}

type LinkResult = { ok: true; link: OnboardingLink };

/** Every chain and franchise company, with its live link — the links console. */
export function useOnboardingLinkGroups() {
  return useQuery({
    queryKey: ALL_KEY,
    queryFn: () => adminFetch<{ ok: true; groups: OnboardingLinkGroup[] }>('admin-onboarding-links'),
    staleTime: 30_000,
  });
}

/**
 * One mutation per action rather than a generic one: each has a different
 * confirmation story in the UI (a rotate revokes the URL corporate is
 * holding), and keeping them apart makes that hard to do by accident.
 *
 * Pass masterCustomerId when acting from one group's own panel; the console
 * passes nothing and just refreshes the whole list.
 */
function useLinkMutation<TInput>(
  masterCustomerId: string | undefined,
  build: (input: TInput) => Record<string, unknown>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      adminFetch<LinkResult>('admin-onboarding-links', {
        method: 'POST',
        body: JSON.stringify(build(input)),
      }),
    onSuccess: () => {
      if (masterCustomerId) void qc.invalidateQueries({ queryKey: key(masterCustomerId) });
      void qc.invalidateQueries({ queryKey: ALL_KEY });
    },
  });
}

/** Mark an account as a Chain HQ / Franchise owner — the half that has to
 *  happen before a link can exist. Reuses the existing group endpoint. */
export function useMarkGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      customer_id: string;
      group_kind: 'chain' | 'franchise' | null;
      group_brand?: string | null;
    }) =>
      adminFetch('admin-customer-group', {
        method: 'POST',
        body: JSON.stringify({ action: 'set_group', ...input }),
      }),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ALL_KEY });
      void qc.invalidateQueries({ queryKey: ['admin-customer-group', vars.customer_id] });
    },
  });
}

export function useCreateOnboardingLink(masterCustomerId: string) {
  return useLinkMutation<{ label?: string; ask_for?: Partial<OnboardingAskFor> } | void>(
    masterCustomerId,
    (input) => ({
      action: 'create',
      master_customer_id: masterCustomerId,
      ...(input ?? {}),
    }),
  );
}

/** Console variant — the group is chosen per call rather than per hook. */
export function useCreateLinkFor() {
  return useLinkMutation<{ master_customer_id: string; label?: string }>(undefined, (input) => ({
    action: 'create',
    ...input,
  }));
}

export function useRotateLinkAny() {
  return useLinkMutation<{ link_id: string }>(undefined, (input) => ({ action: 'rotate', ...input }));
}

export function useDeactivateLinkAny() {
  return useLinkMutation<{ link_id: string }>(undefined, (input) => ({ action: 'deactivate', ...input }));
}

export function useUpdateOnboardingLink(masterCustomerId: string) {
  return useLinkMutation<{
    link_id: string;
    label?: string | null;
    ask_for?: Partial<OnboardingAskFor>;
    active?: boolean;
    expires_at?: string | null;
  }>(masterCustomerId, (input) => ({ action: 'update', ...input }));
}

export function useRotateOnboardingLink(masterCustomerId: string) {
  return useLinkMutation<{ link_id: string }>(masterCustomerId, (input) => ({
    action: 'rotate',
    ...input,
  }));
}

export function useDeactivateOnboardingLink(masterCustomerId: string) {
  return useLinkMutation<{ link_id: string }>(masterCustomerId, (input) => ({
    action: 'deactivate',
    ...input,
  }));
}
